import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDollarSign, faTicketAlt, faChartLine, faUsers, 
  faChevronDown, faChevronUp, faCalendarAlt, faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import './UplineStatsDashboard.css';

const UplineStatsDashboard = ({ statistics, uplineIbo, loading }) => {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    events: false,
    ticketTypes: false,
    paymentMethods: false,
    topPerformers: false,
    actionBreakdown: false
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency 
    }).format(amount);
  };
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
  
  if (loading) {
    return (
      <div className="upline-stats-loading">
        <div className="spinner"></div>
        <p>Calculating statistics...</p>
      </div>
    );
  }
  
  return (
    <div className="upline-stats-dashboard">
      <div className="stats-header">
        <h3>
          <FontAwesomeIcon icon={faChartLine} /> 
          Upline Statistics for IBO: {uplineIbo}
        </h3>
        <p className="stats-subtitle">
          Statistics calculated from {statistics.totalTransactions} filtered transactions
        </p>
      </div>
      
      {/* Key Metrics Overview */}
      <div className="stats-section overview-section">
        <div className="stats-grid">
          <div className="stat-card primary">
            <FontAwesomeIcon icon={faDollarSign} className="stat-icon" />
            <div className="stat-content">
              <h4>Total Revenue</h4>
              <p className="stat-value">{formatCurrency(statistics.totalRevenue, statistics.currency)}</p>
              {statistics.refundedAmount > 0 && (
                <p className="stat-detail">
                  Net: {formatCurrency(statistics.netRevenue, statistics.currency)}
                  <span className="refund-note">
                    ({formatCurrency(statistics.refundedAmount, statistics.currency)} refunded)
                  </span>
                </p>
              )}
            </div>
          </div>
          
          <div className="stat-card">
            <FontAwesomeIcon icon={faTicketAlt} className="stat-icon" />
            <div className="stat-content">
              <h4>Tickets Sold</h4>
              <p className="stat-value">{statistics.totalTicketsSold.toLocaleString()}</p>
              <p className="stat-detail">{statistics.purchases} purchases</p>
            </div>
          </div>
          
          <div className="stat-card">
            <FontAwesomeIcon icon={faUsers} className="stat-icon" />
            <div className="stat-content">
              <h4>Active Downlines</h4>
              <p className="stat-value">{Object.keys(statistics.byDownline).length}</p>
              <p className="stat-detail">with purchases</p>
            </div>
          </div>
          
          <div className="stat-card">
            <FontAwesomeIcon icon={faCalendarAlt} className="stat-icon" />
            <div className="stat-content">
              <h4>Avg per Transaction</h4>
              <p className="stat-value">
                {formatCurrency(statistics.totalRevenue / statistics.totalTransactions || 0, statistics.currency)}
              </p>
              <p className="stat-detail">
                {(statistics.totalTicketsSold / statistics.totalTransactions || 0).toFixed(1)} tickets/txn
              </p>
            </div>
          </div>
        </div>
        
        {/* Alert for refunds/cancellations */}
        {(statistics.refunds > 0 || statistics.cancellations > 0) && (
          <div className="stats-alert">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>
              {statistics.refunds} refund(s) and {statistics.cancellations} cancellation(s) in this period
            </span>
          </div>
        )}
      </div>
      
      {/* Revenue by Event */}
      <div className="stats-section collapsible">
        <div className="section-header" onClick={() => toggleSection('events')}>
          <h4>Revenue by Event</h4>
          <FontAwesomeIcon icon={expandedSections.events ? faChevronUp : faChevronDown} />
        </div>
        {expandedSections.events && (
          <div className="section-content">
            <div className="breakdown-table">
              <table>
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Revenue</th>
                    <th>Tickets</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(statistics.byEvent)
                    .sort(([,a], [,b]) => b.revenue - a.revenue)
                    .map(([event, data]) => (
                      <tr key={event}>
                        <td>{event}</td>
                        <td>{formatCurrency(data.revenue, statistics.currency)}</td>
                        <td>{data.tickets}</td>
                        <td>{data.transactions}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {/* Ticket Types Breakdown */}
      <div className="stats-section collapsible">
        <div className="section-header" onClick={() => toggleSection('ticketTypes')}>
          <h4>Breakdown by Ticket Type</h4>
          <FontAwesomeIcon icon={expandedSections.ticketTypes ? faChevronUp : faChevronDown} />
        </div>
        {expandedSections.ticketTypes && (
          <div className="section-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(statistics.byTicketType).map(([type, data]) => ({
                    name: type,
                    value: data.revenue
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {Object.keys(statistics.byTicketType).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value, statistics.currency)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      
      {/* Payment Methods */}
      <div className="stats-section collapsible">
        <div className="section-header" onClick={() => toggleSection('paymentMethods')}>
          <h4>Payment Methods</h4>
          <FontAwesomeIcon icon={expandedSections.paymentMethods ? faChevronUp : faChevronDown} />
        </div>
        {expandedSections.paymentMethods && (
          <div className="section-content">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={Object.entries(statistics.byPaymentMethod).map(([method, data]) => ({
                name: method,
                revenue: data.revenue,
                transactions: data.transactions
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value, name) => 
                  name === 'revenue' ? formatCurrency(value, statistics.currency) : value
                } />
                <Legend />
                <Bar dataKey="revenue" fill="#8884d8" name="Revenue" />
                <Bar dataKey="transactions" fill="#82ca9d" name="Transactions" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      
      {/* Top Performers */}
      <div className="stats-section collapsible">
        <div className="section-header" onClick={() => toggleSection('topPerformers')}>
          <h4>Top 10 Performing Downlines</h4>
          <FontAwesomeIcon icon={expandedSections.topPerformers ? faChevronUp : faChevronDown} />
        </div>
        {expandedSections.topPerformers && (
          <div className="section-content">
            <div className="top-performers-list">
              {statistics.topDownlines.map((downline, index) => (
                <div key={downline.ibo} className="performer-card">
                  <div className="rank">#{index + 1}</div>
                  <div className="performer-info">
                    <strong>{downline.name}</strong>
                    <span className="ibo-number">IBO: {downline.ibo}</span>
                  </div>
                  <div className="performer-stats">
                    <div className="revenue">{formatCurrency(downline.revenue, statistics.currency)}</div>
                    <div className="tickets">{downline.tickets} tickets</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Action Type Breakdown */}
      <div className="stats-section collapsible">
        <div className="section-header" onClick={() => toggleSection('actionBreakdown')}>
          <h4>Action Type Distribution</h4>
          <FontAwesomeIcon icon={expandedSections.actionBreakdown ? faChevronUp : faChevronDown} />
        </div>
        {expandedSections.actionBreakdown && (
          <div className="section-content">
            <div className="action-breakdown-grid">
              {Object.entries(statistics.byActionType)
                .sort(([,a], [,b]) => b - a)
                .map(([action, count]) => (
                  <div key={action} className="action-stat">
                    <span className="action-name">{action.replace(/_/g, ' ')}</span>
                    <span className="action-count">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UplineStatsDashboard;
